/**
 * Link Command Center (admin) — read-only overzicht van de linkbuilding- + dropcatch-machines.
 * Dropcatch: kandidaten per status/band, score-distributie, de goedkeur-wachtrij, gevangen domeinen.
 * Linkruil: onze BEHEERDE tegelinks (los van historische scan-listings), per-directory + per-money-site.
 * Data via /api/data/links-overview, /links-dropcatch, /links-ruil (views in itdeuldsvcmccmppmxly).
 * Beslissingen (goedkeuren/afkeuren) lopen via Telegram — dit dashboard is bewust read-only.
 */
import React, { useState, useEffect } from 'react';
import { Magnet, Sparkles, Trophy, Inbox, Link2, Target, RefreshCw, X, Ban, RotateCcw, AlertTriangle, Check } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import AuthScreen from './shared/AuthScreen';
import { apiGet, apiPost } from '../utils/apiClient';
import KPICard from './shared/KPICard';
import DashboardNav from './shared/DashboardNav';

const NUM = (v) => Number(v || 0).toLocaleString('nl-NL');
const PCT = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
const DATUM = (v) => (v ? new Date(v).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' }) : '—');
const EUR = (v) => '€' + Math.round(Number(v || 0)).toLocaleString('nl-NL');
const NICHE_LBL = (n) => (Array.isArray(n) ? n.join(', ') : (n || '—'));

// Band uit score (conform score_model): 🟢 score>=50 & dofollow>=3, 🟡 score>=32, 🔴 rest.
const band = (r) => {
  const s = Number(r.score || 0);
  if (s >= 50 && Number(r.dofollow_links || 0) >= 3) return 'groen';
  if (s >= 32) return 'geel';
  return 'rood';
};
const BAND_EMO = { groen: '🟢', geel: '🟡', rood: '🔴' };
const STATUS_LABEL = {
  pending_approval: 'wacht op goedkeuring', catch_scheduled: 'ingepland', catching: 'aan het vangen',
  caught: 'gevangen', live: 'live', provisioning: 'site bouwen', candidate: 'kandidaat',
  rejected: 'afgekeurd', blacklisted: 'geblacklist', seen: 'gezien', low: 'laag', archived: 'gearchiveerd',
  catch_failed: 'catch mislukt', provision_failed: 'gevangen · site-bouw mislukt', site_failed: 'gevangen · site mislukt',
};

// Wachtrij-kolommen: key + label + numeriek (rechts uitlijnen + numeriek sorteren). 'band' is afgeleid van score.
const QUEUE_COLS = [
  { k: 'domein', label: 'domein' },
  { k: 'status', label: 'status' },
  { k: 'band', label: 'band' },
  { k: 'score', label: 'score', num: true },
  { k: 'moz_da', label: 'DA', num: true },
  { k: 'moz_pa', label: 'PA', num: true },
  { k: 'majestic_tf', label: 'TF', num: true },
  { k: 'majestic_cf', label: 'CF', num: true },
  { k: 'dofollow_links', label: 'dofollow', num: true },
  { k: 'ref_domains', label: 'refdom', num: true },
  { k: 'leeftijd_jr', label: 'leeftijd', num: true },
  { k: 'vrijgave', label: 'vrijgave' },
];
const BAND_RANK = { groen: 3, geel: 2, rood: 1 };
// Bevestigingsteksten per actie-soort. 'archive'/'cancel' sturen action='archive', 'restore' stuurt 'restore'.
const ACTION_COPY = {
  approve: { title: 'Goedkeuren & catch inplannen?', cta: 'Goedkeuren', danger: false,
    body: (d) => `${d} wordt ingepland om gevangen te worden op de vrijgavedatum. De registratie kost ~€4,38 zodra de catch slaagt.` },
  archive: { title: 'Archiveren?', cta: 'Archiveren', danger: true,
    body: (d) => `${d} verdwijnt uit de wachtrij. Blijft bewaard (komt niet opnieuw als kandidaat terug) en is later te herstellen.` },
  cancel: { title: 'Geplande catch annuleren?', cta: 'Annuleren & archiveren', danger: true,
    body: (d) => `De ingeplande catch voor ${d} wordt afgezegd en het domein gearchiveerd. Te herstellen via "toon gearchiveerd".` },
  restore: { title: 'Terugzetten naar de wachtrij?', cta: 'Terugzetten', danger: false,
    body: (d) => `${d} gaat terug naar de wachtrij en wacht weer op je goedkeuring.` },
};

function Bar({ value, max, color }) {
  return (
    <div className="lm-bar"><span style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, background: color }} /></div>
  );
}

export default function LinkCommandCenter() {
  const auth = useAuth('admin');
  const [data, setData] = useState({ ov: null, dc: null, ruil: null, netw: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sortCol, setSortCol] = useState('score');
  const [sortDir, setSortDir] = useState('desc');
  const [showArchived, setShowArchived] = useState(false);
  const [confirm, setConfirm] = useState(null); // { domein, action, kind }
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true); setErr('');
    Promise.all([apiGet('/data/links-overview'), apiGet('/data/links-dropcatch'), apiGet('/data/links-ruil'), apiGet('/data/links-netwerk')])
      .then(([ov, dc, ruil, netw]) => setData({ ov, dc, ruil, netw }))
      .catch((e) => setErr(e.message || 'laden mislukt'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (auth.authenticated) load(); }, [auth.authenticated]);

  // Client-side sorteren (zelfde patroon als KeywordDashboard). Nulls altijd onderaan, ongeacht richting.
  const toggle = (c) => {
    if (sortCol === c) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(c); setSortDir(c === 'domein' || c === 'status' ? 'asc' : 'desc'); }
  };
  const arr = (c) => (sortCol === c ? <span className="lm-sortarr">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span> : null);
  const sortRows = (rows) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (r) => {
      if (sortCol === 'band') return BAND_RANK[band(r)] || 0;
      if (sortCol === 'vrijgave') return r.vrijgave ? new Date(r.vrijgave).getTime() : null;
      return r[sortCol];
    };
    const empty = (v) => v === null || v === undefined || v === '';
    return [...(rows || [])].sort((a, b) => {
      const x = val(a), y = val(b);
      if (empty(x) && empty(y)) return 0;
      if (empty(x)) return 1; if (empty(y)) return -1;   // nulls onderaan
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
      return String(x).localeCompare(String(y), 'nl') * dir;
    });
  };

  const doAction = async (domein, action) => {
    setBusy(true);
    try {
      await apiPost('/data/dropcatch/action', { domein, action });
      setConfirm(null);
      load();
    } catch (e) {
      setErr(e.message || 'actie mislukt'); setConfirm(null);
    } finally { setBusy(false); }
  };

  // Eén tabelrij (hergebruikt voor wachtrij + archief). mode bepaalt de actie-knop.
  const renderRow = (r, mode) => (
    <tr key={r.domein}>
      <td className="lm-dom">{r.domein}</td>
      <td>{STATUS_LABEL[r.status] || r.status}</td>
      <td>{BAND_EMO[band(r)]}</td>
      <td className="lm-mono lm-strong lm-r">{r.score}</td>
      <td className="lm-mono lm-r">{r.moz_da ?? '–'}</td>
      <td className="lm-mono lm-r">{r.moz_pa ?? '–'}</td>
      <td className="lm-mono lm-r">{r.majestic_tf ?? '–'}</td>
      <td className="lm-mono lm-r">{r.majestic_cf ?? '–'}</td>
      <td className="lm-mono lm-r">{NUM(r.dofollow_links)}</td>
      <td className="lm-mono lm-r">{NUM(r.ref_domains)}</td>
      <td className="lm-mono lm-r">{r.leeftijd_jr ?? '–'}j</td>
      <td className="lm-mono">{DATUM(r.vrijgave)}</td>
      <td className="lm-r lm-actions">
        {mode === 'archived'
          ? <button className="lm-act" title="Terugzetten naar wachtrij" aria-label="Terugzetten" onClick={() => setConfirm({ domein: r.domein, action: 'restore', kind: 'restore' })}><RotateCcw size={15} /></button>
          : (r.status === 'catch_scheduled'
              ? <button className="lm-act lm-danger" title="Geplande catch annuleren" aria-label="Annuleren" onClick={() => setConfirm({ domein: r.domein, action: 'archive', kind: 'cancel' })}><Ban size={15} /></button>
              : (<>
                  <button className="lm-act lm-ok" title="Goedkeuren — catch inplannen" aria-label="Goedkeuren" onClick={() => setConfirm({ domein: r.domein, action: 'approve', kind: 'approve' })}><Check size={15} /></button>
                  <button className="lm-act lm-danger" title="Archiveren (uit overzicht)" aria-label="Archiveren" onClick={() => setConfirm({ domein: r.domein, action: 'archive', kind: 'archive' })}><X size={15} /></button>
                </>))}
      </td>
    </tr>
  );
  const queueHead = (
    <thead><tr>
      {QUEUE_COLS.map((c) => (
        <th key={c.k} className={`lm-sortable${c.num ? ' lm-r' : ''}`} onClick={() => toggle(c.k)}>{c.label}{arr(c.k)}</th>
      ))}
      <th className="lm-r">acties</th>
    </tr></thead>
  );

  if (auth.loading) return <div className="lm"><style>{LM_CSS}</style><div className="lm-load">Laden…</div></div>;
  if (!auth.authenticated) return <AuthScreen auth={auth} title="LINK MAGNATE" subtitle="Link Command Center" error={err} />;

  const { ov, dc, ruil, netw } = data;
  const histMax = dc ? Math.max(1, ...dc.score_histogram) : 1;
  const dirMax = ruil ? Math.max(1, ...ruil.per_directory.map((d) => d.n_beheerd || 0)) : 1;
  const recMax = netw ? Math.max(1, ...netw.ruilnetwerk.map((r) => r.n_tegelinks || 0)) : 1;

  return (
    <>
      <style>{LM_CSS}</style>
      <DashboardNav
        active="links"
        actions={[{ onClick: load, disabled: loading, title: loading ? 'laden…' : 'ververs', icon: <RefreshCw className={loading ? 'spin' : ''} size={16} /> }]}
      />
      <div className="lm">

      <header className="lm-head">
        <div className="lm-brand"><Magnet className="lm-bi" /> Link Command Center</div>
        <div className="lm-sub">Linkbuilding- &amp; dropcatch-netwerk · read-only overzicht</div>
      </header>

      {err && <div className="lm-err">Fout: {err}</div>}

      {/* KPI-rij */}
      {ov && (
        <div className="lm-kpis">
          <KPICard title="Nieuwe kandidaten (7d)" value={NUM(ov.nieuwe_kandidaten_7d)} subtitle="dropcatch-instroom" icon={Sparkles} />
          <KPICard title="Gems 🟢" value={NUM(ov.gems_groen)} subtitle="score ≥50 · dofollow ≥3" icon={Trophy} />
          <KPICard title="In de wachtrij" value={NUM(ov.queue_pending)} subtitle={`+ ${NUM(ov.scheduled)} ingepland`} icon={Inbox} />
          <KPICard title="Gevangen (7d)" value={NUM(ov.gevangen_7d)} subtitle="naar netwerk" icon={Magnet} />
          <KPICard title="Onze tegelinks live" value={NUM(ov.onze_backlinks_live)} subtitle={`van ${NUM(ov.beheerde_submissions)} beheerd`} icon={Link2} />
          <KPICard title="Ruil-directories" value={NUM(ov.ruil_targets)} subtitle={`${NUM(ov.money_sites)} money-sites`} icon={Target} />
        </div>
      )}

      {/* DROPCATCH */}
      {dc && (
        <section className="lm-card">
          <div className="lm-card-h"><h2>Dropcatch</h2><span className="lm-tag">kandidaten · score · wachtrij</span></div>

          <div className="lm-grid2">
            {/* status x band */}
            <div className="lm-sub-block">
              <div className="lm-bh">Kandidaten per status</div>
              {dc.bands.sort((a, b) => (b.n || 0) - (a.n || 0)).map((b) => (
                <div className="lm-statrow" key={b.status}>
                  <span className="lm-stat-label">{STATUS_LABEL[b.status] || b.status}</span>
                  <span className="lm-bandmix">
                    {b.groen > 0 && <em className="g">🟢{b.groen}</em>}
                    {b.geel > 0 && <em className="y">🟡{b.geel}</em>}
                    {b.rood > 0 && <em className="r">🔴{b.rood}</em>}
                  </span>
                  <span className="lm-stat-n">{NUM(b.n)}</span>
                </div>
              ))}
            </div>
            {/* score-histogram */}
            <div className="lm-sub-block">
              <div className="lm-bh">Score-distributie (verrijkte kandidaten)</div>
              <div className="lm-hist">
                {dc.score_histogram.map((c, i) => (
                  <div className="lm-hbar" key={i} title={`${i * 10}-${i * 10 + 9}: ${c}`}>
                    <span style={{ height: `${(c / histMax) * 100}%`, background: i * 10 >= 50 ? 'var(--lm-teal)' : i * 10 >= 32 ? 'var(--lm-amber)' : 'var(--lm-red)' }} />
                    <small>{i * 10}</small>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* wachtrij — sorteerbaar, met rij-acties (archiveren / geplande catch annuleren) */}
          <div className="lm-bh lm-bh-row" style={{ marginTop: 18 }}>
            <span>Wachtrij — goedkeuren (catch inplannen), archiveren of een geplande catch annuleren. Kan ook via Telegram.</span>
            {(dc.archived || []).length > 0 && (
              <button className="lm-toggle" onClick={() => setShowArchived((s) => !s)}>
                {showArchived ? 'verberg' : 'toon'} gearchiveerd ({dc.archived.length})
              </button>
            )}
          </div>
          <div className="lm-tablewrap">
            <table className="lm-table">
              {queueHead}
              <tbody>
                {dc.queue.length === 0 && <tr><td colSpan={13} className="lm-empty">Geen kandidaten in de wachtrij.</td></tr>}
                {sortRows(dc.queue).map((r) => renderRow(r, 'queue'))}
              </tbody>
            </table>
          </div>

          {showArchived && (
            <>
              <div className="lm-bh" style={{ marginTop: 16 }}>Gearchiveerd</div>
              <div className="lm-tablewrap">
                <table className="lm-table">
                  {queueHead}
                  <tbody>
                    {(dc.archived || []).length === 0 && <tr><td colSpan={13} className="lm-empty">Niets gearchiveerd.</td></tr>}
                    {sortRows(dc.archived).map((r) => renderRow(r, 'archived'))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {dc.caught.length > 0 && (
            <div className="lm-note">🎯 {dc.caught.length} gevangen/live domeinen in het netwerk.</div>
          )}
        </section>
      )}

      {/* LINKRUIL */}
      {ruil && (
        <section className="lm-card">
          <div className="lm-card-h"><h2>Linkruil</h2><span className="lm-tag">onze beheerde tegelinks</span></div>

          <div className="lm-explain">
            <b>Hoe de ruil werkt:</b> wij plaatsen een listing van onze money-site op een directory (een link <b>náár ons</b>).
            In ruil zetten wij een <b>tegelink</b> op de <code>/partners</code>-pagina van een eigen ruil-site, <b>terug naar die directory</b>.
            De cijfers hieronder tellen onze kant van de ruil (de tegelinks die wij beheren).
          </div>

          <div className="lm-ruilstat">
            <div className="lm-big"><b>{NUM(ruil.onze.backlinks_live)}</b><span>tegelinks live</span><small>onze terug-links op /partners, geverifieerd online</small></div>
            <div className="lm-big"><b>{NUM(ruil.onze.totaal)}</b><span>beheerde ruil-links</span><small>listing + tegelink-paren die wij beheren</small></div>
            <div className="lm-big"><b>{PCT(ruil.onze.backlinks_live, ruil.onze.totaal)}%</b><span>live-ratio</span><small>tegelinks online vs. beheerd</small></div>
            <div className="lm-ruilnote">Los hiervan staan er nog duizenden <b>historische scan-listings</b> uit het Dennis-tijdperk (listings zonder onze tegelink) — die tellen niet mee in deze ruil-cijfers.</div>
          </div>

          <div className="lm-grid2">
            <div className="lm-sub-block">
              <div className="lm-bh">Top-directories (onze beheerde links)</div>
              <div className="lm-tablewrap"><table className="lm-table">
                <thead><tr><th>directory</th><th>beheerd</th><th>live</th><th>follow%</th></tr></thead>
                <tbody>
                  {ruil.per_directory.filter((d) => d.n_beheerd > 0).slice(0, 14).map((d) => (
                    <tr key={d.directory}>
                      <td className="lm-dom">{d.directory}</td>
                      <td><Bar value={d.n_beheerd} max={dirMax} color="var(--lm-teal)" /></td>
                      <td className="lm-mono lm-strong">{NUM(d.n_onze_backlinks_live)}/{NUM(d.n_beheerd)}</td>
                      <td className="lm-mono">{d.follow_pct ?? '–'}</td>
                    </tr>
                  ))}
                  {ruil.per_directory.filter((d) => d.n_beheerd > 0).length === 0 && <tr><td colSpan={4} className="lm-empty">Nog geen beheerde links.</td></tr>}
                </tbody>
              </table></div>
            </div>
            <div className="lm-sub-block">
              <div className="lm-bh">Dekking per money-site</div>
              <div className="lm-tablewrap"><table className="lm-table">
                <thead><tr><th>money-site</th><th>beheerd</th><th>tegelinks live</th></tr></thead>
                <tbody>
                  {ruil.per_doelsite.slice(0, 14).map((d) => (
                    <tr key={d.doelsite}>
                      <td className="lm-dom">{d.doelsite}</td>
                      <td className="lm-mono">{NUM(d.n_beheerd)}</td>
                      <td className="lm-mono lm-strong">{NUM(d.n_onze_backlinks_live)}</td>
                    </tr>
                  ))}
                  {ruil.per_doelsite.length === 0 && <tr><td colSpan={3} className="lm-empty">Nog geen dekking.</td></tr>}
                </tbody>
              </table></div>
            </div>
          </div>
        </section>
      )}

      {/* NETWERK */}
      {netw && (
        <section className="lm-card">
          <div className="lm-card-h"><h2>Netwerk</h2><span className="lm-tag">ruilnetwerk · upside · catch</span></div>

          <div className="lm-grid2">
            <div className="lm-sub-block">
              <div className="lm-bh">Het Ruilnetwerk — onze tegelink-bronnen ({netw.ruilnetwerk.length} sites)</div>
              <div className="lm-tablewrap"><table className="lm-table">
                <thead><tr><th>site</th><th>tegelinks</th><th>live</th></tr></thead>
                <tbody>
                  {netw.ruilnetwerk.map((r) => (
                    <tr key={r.reciprocal_site}>
                      <td className="lm-dom">{r.reciprocal_site}</td>
                      <td><Bar value={r.n_tegelinks} max={recMax} color="var(--lm-amber)" /></td>
                      <td className="lm-mono lm-strong">{NUM(r.n_live)}/{NUM(r.n_tegelinks)}</td>
                    </tr>
                  ))}
                  {netw.ruilnetwerk.length === 0 && <tr><td colSpan={3} className="lm-empty">Nog geen tegelinks geplaatst.</td></tr>}
                </tbody>
              </table></div>
            </div>
            <div className="lm-sub-block">
              <div className="lm-bh">Upside-potentieel per niche (naar #1)</div>
              <div className="lm-tablewrap"><table className="lm-table">
                <thead><tr><th>niche</th><th>upside/mnd</th><th>striking 4-10</th><th>top-3</th></tr></thead>
                <tbody>
                  {netw.upside_per_niche.map((u) => (
                    <tr key={u.niche}>
                      <td className="lm-dom">{u.niche}</td>
                      <td className="lm-mono lm-strong" style={{ color: 'var(--lm-teal)' }}>{EUR(u.upside_totaal)}</td>
                      <td className="lm-mono">{NUM(u.n_striking)}</td>
                      <td className="lm-mono">{NUM(u.n_top3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          </div>

          <div className="lm-bh" style={{ marginTop: 18 }}>Catch-netwerk — gevangen domeinen die het netwerk versterken</div>
          {netw.catch_netwerk.length === 0
            ? <div className="lm-empty">Nog geen gevangen domeinen live. De eerste catches (breedbandnoordgroningen e.a.) verschijnen hier zodra ze binnen &amp; online zijn.</div>
            : <div className="lm-tablewrap"><table className="lm-table">
                <thead><tr><th>domein</th><th>niche</th><th>status</th><th>score</th><th>DA</th><th>dofollow</th></tr></thead>
                <tbody>{netw.catch_netwerk.map((c) => (
                  <tr key={c.domein}><td className="lm-dom">{c.domein}</td><td>{NICHE_LBL(c.niche)}</td><td>{STATUS_LABEL[c.status] || c.status}</td><td className="lm-mono lm-strong">{c.score}</td><td className="lm-mono">{c.moz_da ?? '–'}</td><td className="lm-mono">{NUM(c.dofollow_links)}</td></tr>
                ))}</tbody>
              </table></div>}
        </section>
      )}

      {loading && !ov && <div className="lm-load">Data laden…</div>}

      {confirm && (() => {
        const c = ACTION_COPY[confirm.kind];
        return (
          <div className="lm-modal-overlay" onClick={() => !busy && setConfirm(null)}>
            <div className="lm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="lm-modal-h"><AlertTriangle size={18} /> <b>{c.title}</b></div>
              <p className="lm-modal-b">{c.body(confirm.domein)}</p>
              <div className="lm-modal-f">
                <button className="lm-btn-ghost" disabled={busy} onClick={() => setConfirm(null)}>Terug</button>
                <button className={`lm-btn-confirm${c.danger ? ' lm-btn-danger' : ''}`} disabled={busy} onClick={() => doAction(confirm.domein, confirm.action)}>
                  {busy ? 'bezig…' : c.cta}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      </div>
    </>
  );
}

const LM_CSS = `
.lm{ --lm-bg:#f6f7f9; --lm-panel:#ffffff; --lm-ink:#0f172a; --lm-muted:#64748b; --lm-border:#e6e8ec;
     --lm-amber:#c9901f; --lm-teal:#0d9488; --lm-red:#dc2626;
     min-height:100vh; background:var(--lm-bg); color:var(--lm-ink);
     font-family:'Plus Jakarta Sans',-apple-system,sans-serif; padding:20px clamp(14px,4vw,40px); }
[data-theme="dark"] .lm{ --lm-bg:#0c0e13; --lm-panel:#161a24; --lm-ink:#e9e7e1; --lm-muted:#928e84;
     --lm-border:#262c39; --lm-amber:#f2b339; --lm-teal:#34d3bd; --lm-red:#f0768b; }
.lm-load{ padding:60px; text-align:center; color:var(--lm-muted); }
.lm-err{ background:rgba(220,38,38,.08); border:1px solid var(--lm-red); color:var(--lm-red); padding:10px 14px; border-radius:10px; margin-bottom:16px; font-size:.9rem; }
.lm-head{ display:flex; align-items:center; gap:14px; flex-wrap:wrap; margin-bottom:22px; }
.lm-brand{ display:flex; align-items:center; gap:10px; font-weight:800; font-size:clamp(1.4rem,3vw,2rem); letter-spacing:-.02em; }
.lm-bi{ color:var(--lm-amber); }
.lm-sub{ color:var(--lm-muted); font-size:.92rem; }
.lm-refresh{ margin-left:auto; display:flex; align-items:center; gap:6px; background:var(--lm-panel); color:var(--lm-ink); border:1px solid var(--lm-border); border-radius:9px; padding:7px 13px; font-size:.85rem; cursor:pointer; font-family:inherit; }
.lm-refresh:hover{ border-color:var(--lm-amber); }
.spin{ animation:lmspin 1s linear infinite; } @keyframes lmspin{ to{ transform:rotate(360deg); } }
.lm-kpis{ display:grid; grid-template-columns:repeat(6,1fr); gap:12px; margin-bottom:22px; }
@media(max-width:1100px){ .lm-kpis{ grid-template-columns:repeat(3,1fr); } }
@media(max-width:620px){ .lm-kpis{ grid-template-columns:repeat(2,1fr); } }
.lm-card{ background:var(--lm-panel); border:1px solid var(--lm-border); border-radius:16px; padding:20px clamp(14px,2.5vw,24px); margin-bottom:20px; }
.lm-card-h{ display:flex; align-items:baseline; gap:12px; border-bottom:1px solid var(--lm-border); padding-bottom:12px; margin-bottom:16px; }
.lm-card-h h2{ font-size:1.15rem; font-weight:800; margin:0; }
.lm-tag{ font-size:.72rem; text-transform:uppercase; letter-spacing:.14em; color:var(--lm-teal); font-family:'JetBrains Mono',monospace; }
.lm-grid2{ display:grid; grid-template-columns:1fr 1fr; gap:20px; } @media(max-width:820px){ .lm-grid2{ grid-template-columns:1fr; } }
.lm-bh{ font-size:.74rem; text-transform:uppercase; letter-spacing:.1em; color:var(--lm-muted); margin-bottom:10px; font-weight:700; }
.lm-statrow{ display:flex; align-items:center; gap:10px; padding:6px 0; border-bottom:1px solid var(--lm-border); font-size:.9rem; }
.lm-stat-label{ flex:1; }
.lm-bandmix{ display:flex; gap:8px; font-family:'JetBrains Mono',monospace; font-size:.78rem; }
.lm-bandmix em{ font-style:normal; }
.lm-stat-n{ font-family:'JetBrains Mono',monospace; font-weight:700; min-width:34px; text-align:right; }
.lm-hist{ display:flex; align-items:flex-end; gap:4px; height:120px; padding-top:8px; }
.lm-hbar{ flex:1; display:flex; flex-direction:column; justify-content:flex-end; align-items:center; height:100%; }
.lm-hbar span{ width:100%; border-radius:3px 3px 0 0; min-height:2px; transition:height .4s; }
.lm-hbar small{ font-size:.6rem; color:var(--lm-muted); margin-top:4px; font-family:'JetBrains Mono',monospace; }
.lm-bar{ height:7px; background:var(--lm-border); border-radius:4px; overflow:hidden; min-width:60px; }
.lm-bar span{ display:block; height:100%; border-radius:4px; }
.lm-tablewrap{ overflow-x:auto; }
.lm-table{ width:100%; border-collapse:collapse; font-size:.84rem; }
.lm-table th{ text-align:left; font-size:.66rem; text-transform:uppercase; letter-spacing:.05em; color:var(--lm-muted); padding:7px 9px; border-bottom:1px solid var(--lm-border); white-space:nowrap; }
.lm-table td{ padding:7px 9px; border-bottom:1px solid var(--lm-border); white-space:nowrap; }
.lm-table tr:hover td{ background:rgba(125,125,125,.05); }
.lm-dom{ font-weight:600; }
.lm-mono{ font-family:'JetBrains Mono',monospace; font-size:.8rem; }
.lm-strong{ font-weight:700; }
.lm-empty{ text-align:center; color:var(--lm-muted); padding:18px; }
.lm-r{ text-align:right; }
.lm-table th.lm-r{ text-align:right; }
.lm-sortable{ cursor:pointer; user-select:none; }
.lm-sortable:hover{ color:var(--lm-ink); }
.lm-sortarr{ color:var(--lm-amber); }
.lm-bh-row{ display:flex; align-items:center; gap:10px; }
.lm-toggle{ margin-left:auto; background:var(--lm-panel); border:1px solid var(--lm-border); border-radius:8px; padding:4px 10px; font-size:.72rem; cursor:pointer; color:var(--lm-muted); font-family:inherit; text-transform:none; letter-spacing:0; }
.lm-toggle:hover{ border-color:var(--lm-amber); color:var(--lm-ink); }
.lm-actions{ white-space:nowrap; width:1%; }
.lm-act{ background:transparent; border:1px solid var(--lm-border); border-radius:7px; padding:4px 6px; cursor:pointer; color:var(--lm-muted); display:inline-flex; align-items:center; line-height:0; }
.lm-act:hover{ color:var(--lm-ink); border-color:var(--lm-muted); }
.lm-act.lm-danger:hover{ color:var(--lm-red); border-color:var(--lm-red); background:rgba(220,38,38,.06); }
.lm-act.lm-ok:hover{ color:var(--lm-teal); border-color:var(--lm-teal); background:rgba(13,148,136,.08); }
.lm-actions .lm-act + .lm-act{ margin-left:6px; }
.lm-modal-overlay{ position:fixed; inset:0; background:rgba(8,10,15,.55); backdrop-filter:blur(3px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.lm-modal{ background:var(--lm-panel); border:1px solid var(--lm-border); border-radius:14px; max-width:430px; width:100%; padding:20px 22px; box-shadow:0 20px 60px rgba(0,0,0,.3); }
.lm-modal-h{ display:flex; align-items:center; gap:9px; font-size:1.02rem; color:var(--lm-ink); }
.lm-modal-h svg{ color:var(--lm-amber); }
.lm-modal-b{ color:var(--lm-muted); font-size:.88rem; line-height:1.5; margin:12px 0 18px; }
.lm-modal-f{ display:flex; justify-content:flex-end; gap:10px; }
.lm-btn-ghost,.lm-btn-confirm{ border-radius:9px; padding:8px 15px; font-size:.85rem; cursor:pointer; font-family:inherit; border:1px solid var(--lm-border); }
.lm-btn-ghost{ background:transparent; color:var(--lm-muted); }
.lm-btn-ghost:hover{ color:var(--lm-ink); }
.lm-btn-confirm{ background:var(--lm-teal); color:#fff; border-color:transparent; font-weight:700; }
.lm-btn-confirm.lm-btn-danger{ background:var(--lm-red); }
.lm-btn-confirm:disabled,.lm-btn-ghost:disabled{ opacity:.6; cursor:default; }
.lm-note{ margin-top:12px; font-size:.86rem; color:var(--lm-muted); }
.lm-ruilstat{ display:flex; gap:24px; align-items:center; flex-wrap:wrap; margin-bottom:18px; }
.lm-big{ display:flex; flex-direction:column; max-width:230px; }
.lm-big b{ font-size:1.9rem; font-weight:800; font-family:'JetBrains Mono',monospace; color:var(--lm-teal); line-height:1; }
.lm-big span{ font-size:.82rem; color:var(--lm-ink); margin-top:4px; font-weight:600; }
.lm-big small{ font-size:.7rem; color:var(--lm-muted); margin-top:3px; line-height:1.35; }
.lm-explain{ background:rgba(13,148,136,.06); border:1px solid var(--lm-border); border-left:3px solid var(--lm-teal); border-radius:8px; padding:12px 14px; font-size:.84rem; color:var(--lm-ink); line-height:1.55; margin-bottom:16px; }
.lm-explain code{ font-family:'JetBrains Mono',monospace; font-size:.78rem; background:rgba(125,125,125,.14); padding:1px 5px; border-radius:4px; }
.lm-ruilnote{ flex:1; min-width:240px; font-size:.82rem; color:var(--lm-muted); border-left:2px solid var(--lm-amber); padding-left:12px; }
`;
